"use strict";
exports.__esModule = true;
exports.createUnclusterPoints = exports.createClusterLabels = exports.createClusterPoints = exports.createPushpinDataLayer = exports.createPushpinFilter = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var partial_1 = tslib_1.__importDefault(require("lodash/partial"));
var geoChart_1 = require("./constants/geoChart");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var common_1 = require("./helpers/geoChart/common");
function createPushpinSizeOptions(geoData, geoPointsConfig) {
    var size = geoData.size;
    var defaultRadius = geoChart_1.PUSHPIN_SIZE_OPTIONS_MAP.min["default"] / 2;
    if (size === undefined || size.data.length === 0) {
        return defaultRadius;
    }
    var _a = common_1.getMinMax(size.data), minSizeFromData = _a.min, maxSizeFromData = _a.max;
    if (maxSizeFromData === minSizeFromData) {
        return defaultRadius;
    }
    var _b = geoPointsConfig || {}, _c = _b.minSize, minSizeFromConfig = _c === void 0 ? "default" : _c, _d = _b.maxSize, maxSizeFromConfig = _d === void 0 ? "default" : _d;
    var minSizeInPixel = geoChart_1.PUSHPIN_SIZE_OPTIONS_MAP.min[minSizeFromConfig];
    var maxSizeInPixel = geoChart_1.PUSHPIN_SIZE_OPTIONS_MAP.max[maxSizeFromConfig];
    var base = Math.pow(maxSizeInPixel / minSizeInPixel, 0.25);
    var getStopPointSize = partial_1["default"](getPointSize, minSizeInPixel, base);
    // The mouseenter event uses queryRenderedFeatures to determine when the mouse is touching a feature
    // And queryRenderedFeatures is not supporting nested object in expression
    // https://github.com/mapbox/mapbox-gl-js/issues/7194
    return [
        "step",
        ["get", "pushpinSize"],
        Math.round(minSizeInPixel / 2),
        getStopPointSize(1),
        Math.round(getStopPointSize(1) / 2),
        getStopPointSize(2),
        Math.round(getStopPointSize(2) / 2),
        getStopPointSize(3),
        Math.round(getStopPointSize(3) / 2),
        maxSizeInPixel,
        Math.round(maxSizeInPixel / 2),
    ];
}
function getPointSize(minSizeInPixel, base, exponent) {
    var stepValue = minSizeInPixel * Math.pow(base, exponent);
    return parseFloat(stepValue.toFixed(2));
}
function createPushpinFilter(selectedSegmentItems) {
    return [
        "match",
        ["get", "uri", ["object", ["get", sdk_ui_1.BucketNames.SEGMENT]]],
        selectedSegmentItems.length ? selectedSegmentItems : [geoChart_1.EMPTY_SEGMENT_VALUE],
        true,
        false,
    ]; // true/false are the output values, from the https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#match
}
exports.createPushpinFilter = createPushpinFilter;
function createPushpinColorOptions() {
    return ["string", ["get", "background", ["object", ["get", "color"]]]];
}
function createPushpinBorderOptions() {
    return ["string", ["get", "border", ["object", ["get", "color"]]], geoChart_1.DEFAULT_PUSHPIN_BORDER_COLOR_VALUE];
}
function createPushpinDataLayer(dataSourceName, geoData, config) {
    var _a;
    var _b = config || {}, _c = _b.selectedSegmentItems, selectedSegmentItems = _c === void 0 ? [] : _c, _d = _b.points, geoPointsConfig = _d === void 0 ? {} : _d;
    var layer = {
        id: geoChart_1.DEFAULT_LAYER_NAME,
        type: geoChart_1.PUSHPIN_STYLE_CIRCLE,
        source: dataSourceName,
        paint: tslib_1.__assign(tslib_1.__assign({}, geoChart_1.DEFAULT_PUSHPIN_OPTIONS), (_a = {}, _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_COLOR] = createPushpinColorOptions(), _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_STROKE_COLOR] = createPushpinBorderOptions(), _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_SIZE] = createPushpinSizeOptions(geoData, geoPointsConfig), _a))
    };
    if (selectedSegmentItems.length > 0) {
        layer.filter = createPushpinFilter(selectedSegmentItems);
    }
    return layer;
}
exports.createPushpinDataLayer = createPushpinDataLayer;
/**
 * Create layer for clustered points/pins which have 'properties.point_count' indicates number of same points is clustered together
 */
function createClusterPoints(dataSourceName) {
    var _a;
    return {
        id: geoChart_1.DEFAULT_CLUSTER_LAYER_NAME,
        type: geoChart_1.PUSHPIN_STYLE_CIRCLE,
        source: dataSourceName,
        filter: geoChart_1.DEFAULT_CLUSTER_FILTER,
        paint: tslib_1.__assign(tslib_1.__assign({}, geoChart_1.DEFAULT_CLUSTER_POINT_BORDERS), (_a = {}, _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_COLOR] = geoChart_1.DEFAULT_CLUSTER_POINT_COLORS, _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_SIZE] = geoChart_1.DEFAULT_CLUSTER_POINT_SIZES, _a))
    };
}
exports.createClusterPoints = createClusterPoints;
/**
 * Create layer for cluster labels which indicate number of points/pins is clustered
 */
function createClusterLabels(dataSourceName) {
    return tslib_1.__assign(tslib_1.__assign({}, geoChart_1.DEFAULT_CLUSTER_LABELS_CONFIG), { source: dataSourceName, filter: geoChart_1.DEFAULT_CLUSTER_FILTER });
}
exports.createClusterLabels = createClusterLabels;
/**
 * Create layer for un-clustered points which are not close to others
 */
function createUnclusterPoints(dataSourceName) {
    var _a;
    return {
        id: geoChart_1.DEFAULT_LAYER_NAME,
        type: geoChart_1.PUSHPIN_STYLE_CIRCLE,
        source: dataSourceName,
        filter: ["!", geoChart_1.DEFAULT_CLUSTER_FILTER],
        paint: tslib_1.__assign(tslib_1.__assign({}, geoChart_1.DEFAULT_PUSHPIN_OPTIONS), (_a = {}, _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_COLOR] = createPushpinColorOptions(), _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_STROKE_COLOR] = geoChart_1.DEFAULT_PUSHPIN_BORDER_COLOR_VALUE, _a[geoChart_1.PUSHPIN_STYLE_CIRCLE_SIZE] = geoChart_1.PUSHPIN_SIZE_OPTIONS_MAP.min["default"] / 2, _a))
    };
}
exports.createUnclusterPoints = createUnclusterPoints;
//# sourceMappingURL=geoChartDataLayers.js.map